import { CONTROL_TYPES } from "Core/Constant";
import { GenericButton } from "Core/Controls/GenericButton/GenericButton";
import { Grid } from "Core/Controls/Grid/Grid";
import { IControl } from "Core/Controls/IControl";
import type { TabTableData } from "Core/Screens/Common/LinkEditor/Components/TabTables/Models/TabTableData";
import { name } from "pubsub-js";
import { P } from "Core/Common/Promise";
import type { GridRow } from "Core/Controls/Grid/BaseGrid/GridRow/GridRow";

export interface ITabGridRowData {
    cells: Array<{
        value: string;
        displayValue: string;
        fieldName: string;
    }>;
}

export interface ITabGridData {
    rows: ITabGridRowData[];
}

export interface IAddNewRowOptions {
    typeId?: number;
    kindId?: number;
}

export const tabGridFacade = (tabTable: TabTableData)=> {

    const setVisible = (value: boolean) =>{
        tabTable.SetVisible(value);
    }

    const onChanged = (callback: (data?: { value: string; displayValue: string }) => void) => {
        tabTable.Grid.BaseGrid.On('RECORD_SAVED', this, ()=> callback());
        tabTable.Grid.BaseGrid.On('RECORDS_LINKED', this, ()=> callback());

        // Track row count changes with debounce
        let previousRowCount = tabTable.Grid.BaseGrid.RowsObserverable().length;
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        tabTable.Grid.BaseGrid.RowsObserverable.subscribe(() => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            debounceTimer = setTimeout(() => {
                const currentRowCount = tabTable.Grid.BaseGrid.RowsObserverable().length;
                if (currentRowCount !== previousRowCount) {
                    previousRowCount = currentRowCount;
                    callback();
                }
                debounceTimer = null;
            }, 0);
        });
    }

    const getData = (): ITabGridData => {
        return{
            rows: tabTable.Grid.BaseGrid.Rows.map((row) => {
                return {
                    cells: row.DataCells.map((cell) => {
                        return {
                            value: cell.Value,
                            displayValue: cell.DisplayValue,
                            fieldName: cell.FieldName
                        };
                    })
                };
            })
        }
    }

    /**
     * Set the grid view by name
     * @param name - The view name to switch to
     * @returns Promise that resolves when data is loaded
     */
    const setView = (name: string): Promise<void> => {
        return tabTable.Grid.SetView(name);
    }

    /**
     * Get the current number of rows in the grid
     */
    const getRowCount = (): number => {
        return tabTable.Grid.BaseGrid.RowsObserverable().length;
    }

    /**
     * Add a new row to the grid via API call
     * @param options - Optional typeId and kindId for the new record
     * @returns Promise that resolves with the newly created GridRow
     */
    const addNewRow = (options: IAddNewRowOptions = {}): P.Promise<GridRow> => {
        const deferredResult = P.defer<GridRow>();

        tabTable.Grid.BaseGrid.NewRecord(options.typeId || null, options.kindId || null)
            .then((newRow: GridRow) => {
                deferredResult.resolve(newRow);
            })
            .fail((err) => {
                deferredResult.reject(err);
            });

        return deferredResult.promise();
    }

    /**
     * Add multiple new rows to the grid
     * @param count - Number of rows to add
     * @param options - Optional typeId and kindId for the new records
     * @returns Promise that resolves with an array of created GridRows
     */
    const addNewRows = (count: number, options: IAddNewRowOptions = {}): P.Promise<GridRow[]> => {
        const deferredResult = P.defer<GridRow[]>();
        const rows: GridRow[] = [];

        if (count <= 0) {
            deferredResult.resolve(rows);
            return deferredResult.promise();
        }

        let addedCount = 0;
        const addNextRow = () => {
            if (addedCount >= count) {
                deferredResult.resolve(rows);
                return;
            }

            tabTable.Grid.BaseGrid.NewRecord(options.typeId || null, options.kindId || null)
                .then((newRow: GridRow) => {
                    rows.push(newRow);
                    addedCount++;
                    addNextRow();
                })
                .fail((err) => {
                    deferredResult.reject(err);
                });
        };

        addNextRow();
        return deferredResult.promise();
    }

    /**
     * Clear all rows from the grid
     */
    const clearRows = (): void => {
        tabTable.Grid.BaseGrid.Clear();
    }

    /**
     * Set edit mode for all rows in the grid
     * @param enabled - Whether to enable or disable edit mode
     */
    const setEditMode = (enabled: boolean): void => {
        if (enabled) {
            tabTable.Grid.BaseGrid.EditAll();
        } else {
            tabTable.Grid.BaseGrid.SaveAll();
        }
    }

    /**
     * Check if the grid has unsaved edits
     */
    const hasUnsavedEdits = (): boolean => {
        return tabTable.Grid.BaseGrid.HasEditRows();
    }

    /**
     * Save all edited rows
     * @returns true if all saves were successful
     */
    const saveAll = (): boolean => {
        return tabTable.Grid.BaseGrid.SaveAll();
    }

    /**
     * Get the grid's table name
     */
    const getTableName = (): string => {
        return tabTable.TableName;
    }

    // Store removed columns for restoration: fieldName -> { column, originalIndex }
    const removedColumns: Map<string, { column: any; originalIndex: number }> = new Map();

    /**
     * Check if a view with the given name exists
     */
    const hasView = (name: string): boolean => {
        return tabTable.Grid.HasView(name);
    }

    /**
     * Get all available view names
     */
    const getViewNames = (): string[] => {
        return tabTable.Grid.GetViewNames();
    }

    /**
     * Check if a column with the given field name exists (includes hidden columns)
     */
    const hasColumn = (fieldName: string): boolean => {
        // Check visible columns
        const visibleExists = tabTable.Grid.HasColumn(fieldName);
        // Check hidden columns
        const hiddenExists = removedColumns.has(fieldName);
        return visibleExists || hiddenExists;
    }

    /**
     * Get all column field names in the grid (includes hidden columns)
     */
    const getColumnFieldNames = (): string[] => {
        const visibleNames = tabTable.Grid.GetColumnFieldNames();
        const hiddenNames = Array.from(removedColumns.keys());
        return [...visibleNames, ...hiddenNames];
    }

    /**
     * Hide a column by field name - actually removes it from the grid
     * @param fieldName - The field name of the column to hide
     * @returns true if the column was found and hidden
     */
    const hideColumn = (fieldName: string): boolean => {
        // Already hidden
        if (removedColumns.has(fieldName)) {
            return true;
        }

        const headerColumns = (tabTable.Grid.BaseGrid as any)._headerColumns as KnockoutObservableArray<any>;
        const columns = headerColumns();
        const columnIndex = columns.findIndex(
            (col: any) => col.Model.FieldMetadata && col.Model.FieldMetadata.Name === fieldName
        );

        if (columnIndex === -1) {
            return false;
        }

        // Store the column and its original index for restoration
        const column = columns[columnIndex];
        removedColumns.set(fieldName, { column, originalIndex: columnIndex });

        // Remove from the observable array
        headerColumns.splice(columnIndex, 1);

        return true;
    }

    /**
     * Show a previously hidden column by field name - restores it to the grid
     * @param fieldName - The field name of the column to show
     * @returns true if the column was found and shown
     */
    const showColumn = (fieldName: string): boolean => {
        const stored = removedColumns.get(fieldName);
        if (!stored) {
            return false;
        }

        const headerColumns = (tabTable.Grid.BaseGrid as any)._headerColumns as KnockoutObservableArray<any>;
        const currentColumns = headerColumns();

        // Calculate insertion index (clamp to valid range)
        const insertIndex = Math.min(stored.originalIndex, currentColumns.length);

        // Insert the column back
        headerColumns.splice(insertIndex, 0, stored.column);

        // Remove from stored
        removedColumns.delete(fieldName);

        return true;
    }

    /**
     * Set an error message and mark the grid as invalid
     * This will show the warning badge and block save
     * @param message - The error message to display
     */
    const setError = (message: string): void => {
        tabTable.Grid.SetError(message);
    }

    /**
     * Clear the error message and mark the grid as valid
     */
    const clearError = (): void => {
        tabTable.Grid.ClearError();
    }

    /**
     * Check if the grid currently has an error set
     */
    const hasError = (): boolean => {
        return tabTable.Grid.HasError();
    }

    /**
     * Get the current error message
     */
    const getErrorMessage = (): string => {
        return tabTable.Grid.GetErrorMessage();
    }

    /**
     * Check if a column is currently hidden
     */
    const isColumnHidden = (fieldName: string): boolean => {
        return removedColumns.has(fieldName);
    }

    /**
     * Activate/select this grid's tab in the LinkEditor
     * Makes this tab the currently visible tab
     */
    const activateTab = (): void => {
        tabTable.ActivateTab();
    }

    // Button visibility methods
    const setShowLinkButton = (show: boolean): void => {
        tabTable.Grid.SetShowLinkButton(show);
    }

    const setShowScanAndLinkButton = (show: boolean): void => {
        tabTable.Grid.SetShowScanAndLinkButton(show);
    }

    const setShowAddButton = (show: boolean): void => {
        tabTable.Grid.SetShowAddButton(show);
    }

    const setShowAddAndLinkButton = (show: boolean): void => {
        tabTable.Grid.SetShowAddAndLinkButton(show);
    }

    const getShowLinkButton = (): boolean => {
        return tabTable.Grid.GetShowLinkButton();
    }

    const getShowScanAndLinkButton = (): boolean => {
        return tabTable.Grid.GetShowScanAndLinkButton();
    }

    const typeName = CONTROL_TYPES.Grid;

    return {
        setVisible,
        typeName,
        getData,
        setView,
        onChanged,
        getRowCount,
        addNewRow,
        addNewRows,
        clearRows,
        setEditMode,
        hasUnsavedEdits,
        saveAll,
        getTableName,
        // View methods
        hasView,
        getViewNames,
        // Column methods
        hasColumn,
        getColumnFieldNames,
        hideColumn,
        showColumn,
        isColumnHidden,
        // Error methods
        setError,
        clearError,
        hasError,
        getErrorMessage,
        // Tab methods
        activateTab,
        // Button visibility methods
        setShowLinkButton,
        setShowScanAndLinkButton,
        setShowAddButton,
        setShowAddAndLinkButton,
        getShowLinkButton,
        getShowScanAndLinkButton
    }
}